import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  Text,
  Alert,
  Loader,
  Badge,
  Table,
  Divider,
  DetailField,
  formatAddress,
} from '@vipso/shared';
import { useGetProcurementQuery } from '../api/tenantApi';
import { useDate } from '../hooks/useDate';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

function formatCurrency(value?: number | null) {
  if (value == null) return null;
  return `₹${Number(value).toFixed(2)}`;
}

export function ProcurementDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { format } = useDate();
  const { data, isLoading, isError } = useGetProcurementQuery(id!, { skip: !id });

  const proc = data?.getProcurement;
  const party = proc?.customer ?? proc?.vendor;
  const partyAddress = formatAddress(proc?.customer?.address) || formatAddress(proc?.vendor?.address);
  const empty = t('common.emptyValue');

  function handleDownloadInvoice() {
    if (proc?.procUrl) {
      window.open(proc.procUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    window.alert(t('orders.invoiceUnavailable'));
  }

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isError || !proc) {
    return (
      <Alert color="red" title={t('orders.notFound')}>
        <Button variant="light" mt="sm" onClick={() => navigate('/orders')}>
          {t('orders.backToOrders')}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('orders.detailTitle', { party: party?.name || t('orders.title') })}
        description={
          proc.ordNumber
            ? t('orders.orderNumber', { number: proc.ordNumber })
            : t('orders.orderDetails')
        }
        actions={
          <Group>
            <Button variant="default" onClick={() => navigate('/orders')}>
              {t('common.back')}
            </Button>
            <Button onClick={handleDownloadInvoice}>{t('orders.downloadInvoice')}</Button>
          </Group>
        }
      />

      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <Group gap="sm">
            <Badge color={proc.procType === 'SALES' ? 'green' : 'blue'}>
              {proc.procType === 'SALES' ? t('orders.sales') : t('orders.purchase')}
            </Badge>
            <ApprovalStatusBadge status={proc.approvalStatus} />
            <Text size="sm" c="dimmed">
              {proc.procType === 'SALES' ? t('orders.fromCustomer') : t('orders.fromVendor')}
            </Text>
          </Group>
          <DetailField
            label={t('orders.orderNumberLabel')}
            value={proc.ordNumber ? `#${proc.ordNumber}` : undefined}
          />
          <DetailField label={t('common.createdOn')} value={format(proc.createdAt, 'date')} />
          <DetailField
            label={proc.customer ? t('common.customer') : t('common.vendor')}
            value={party?.name}
          />
          <DetailField label={t('common.phone')} value={party?.phone} />
          <DetailField label={t('common.address')} value={partyAddress} />
          <DetailField
            label={t('orders.labour')}
            value={
              proc.labour
                ? `${proc.labour.name}${proc.labour.phone ? ` (${proc.labour.phone})` : ''}`
                : undefined
            }
          />
          <DetailField
            label={t('orders.transport')}
            value={
              proc.transport
                ? `${proc.transport.name}${proc.transport.phone ? ` (${proc.transport.phone})` : ''}`
                : undefined
            }
          />
          <DetailField label={t('orders.vehicleNumber')} value={proc.vehicleNo} />
          <DetailField label={t('orders.biltyNumber')} value={proc.biltyNo} />
          <DetailField label={t('orders.receiptNumber')} value={proc.receiptNumber} />
          <DetailField label={t('orders.gatePassNumber')} value={proc.gatePassNumber} />
          <DetailField label={t('orders.loadNumber')} value={proc.loadNumber} />
          <DetailField label={t('orders.bagNumber')} value={proc.bagNumber} />
          <DetailField
            label={t('orders.approvedBy')}
            value={
              proc.approvedBy && typeof proc.approvedBy === 'object' && 'name' in proc.approvedBy
                ? String((proc.approvedBy as { name?: string }).name ?? '')
                : undefined
            }
          />
          <DetailField
            label={t('orders.approvedAt')}
            value={proc.approvedAt ? format(proc.approvedAt, 'date') : undefined}
          />
          <DetailField label={t('common.remarks')} value={proc.remarks} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="PROC"
        subjectId={proc.id}
        entityApprovalStatus={proc.approvalStatus}
      />

      <Paper p="lg" withBorder radius="md">
        <Text fw={600} mb="md">
          {t('common.products')}
        </Text>
        <Table.ScrollContainer minWidth={500}>
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('orders.colProduct')}</Table.Th>
                <Table.Th>{t('orders.colLocation')}</Table.Th>
                <Table.Th>{t('orders.colQty')}</Table.Th>
                <Table.Th>{t('orders.colLoss')}</Table.Th>
                <Table.Th>{t('orders.colPrice')}</Table.Th>
                <Table.Th>{t('orders.colSubtotal')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(proc.products ?? []).map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{line.product?.name || empty}</Text>
                      {line.addOnItemQty ? (
                        <Text size="xs" c="dimmed">
                          {t('orders.addOn', {
                            qty: line.addOnItemQty,
                            name: line.product?.addOnItemName ?? '',
                          })}
                        </Text>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {[line.chamber?.name, line.floor?.name, line.rack?.name]
                        .filter(Boolean)
                        .join(' / ') || empty}
                    </Text>
                  </Table.Td>
                  <Table.Td>{line.quantity ?? empty}</Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {Number(line.lossPercent || 0)}% / {Number(line.lossKg || 0)} kg
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatCurrency(line.pricePerUnit) ?? empty}</Table.Td>
                  <Table.Td>{formatCurrency(line.productPrice) ?? empty}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Paper p="lg" withBorder radius="md">
        <Stack gap="sm">
          {(proc.chargesAmount ?? 0) > 0 ? (
            <Group justify="space-between">
              <Text size="sm">{t('orders.charges')}</Text>
              <Text size="sm" fw={600}>
                {formatCurrency(proc.chargesAmount)}
              </Text>
            </Group>
          ) : null}
          {(proc.discountAmount ?? 0) > 0 ? (
            <Group justify="space-between">
              <Text size="sm">{t('orders.discount')}</Text>
              <Text size="sm" fw={600}>
                {formatCurrency(proc.discountAmount)}
              </Text>
            </Group>
          ) : null}
          {(proc.taxAmount ?? 0) > 0 ? (
            <Group justify="space-between">
              <Text size="sm">{t('orders.tax')}</Text>
              <Text size="sm" fw={600}>
                {formatCurrency(proc.taxAmount)}
              </Text>
            </Group>
          ) : null}
          <Divider />
          <Group justify="space-between">
            <Text fw={700}>{t('orders.total')}</Text>
            <Text fw={700}>{formatCurrency(proc.procAmount) ?? empty}</Text>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
